/**
 * Auth Service — token-based authentication.
 *
 * After SSO login the server returns a `sessionToken` in the response body.
 * We store it in sessionStorage under 'auth_token' and send it as the
 * X-Session-Token header on every authenticated request.
 * No cookies / credentials: 'include' are used.
 *
 * sessionStorage keys:
 *   auth_token  — bearer token string
 *   auth_user   — JSON-serialised user profile (for instant UI restore)
 */

const AUTH_TOKEN_KEY  = 'auth_token';
const AUTH_USER_KEY   = 'auth_user';
const AUTH_LEVEL_KEY  = 'auth_level';

// ─── Base URL ──────────────────────────────────────────────────────────────────

let _baseURL = null;
async function getBaseURL() {
    if (_baseURL !== null) return _baseURL;
    try {
        const res = await fetch('/config.json');
        const cfg = await res.json();
        _baseURL = process.env.NODE_ENV === 'development'
            ? cfg.developmentBaseURL
            : cfg.productionBaseURL;
        if (_baseURL && _baseURL.endsWith('/')) _baseURL = _baseURL.slice(0, -1);
    } catch {
        _baseURL = '';
    }
    return _baseURL;
}

// ─── Token helpers ─────────────────────────────────────────────────────────────

export function getStoredToken() {
    try { return localStorage.getItem(AUTH_TOKEN_KEY) || null; } catch { return null; }
}

function storeToken(token) {
    try { localStorage.setItem(AUTH_TOKEN_KEY, token); } catch { /* ignore */ }
}

function clearStoredToken() {
    try { localStorage.removeItem(AUTH_TOKEN_KEY); } catch { /* ignore */ }
}

// ─── User profile helpers ──────────────────────────────────────────────────────

export function getStoredUser() {
    try {
        const raw = localStorage.getItem(AUTH_USER_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
}

function storeUser(user) {
    try { localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user)); } catch { /* ignore */ }
}

export function clearStoredUser() {
    try { localStorage.removeItem(AUTH_USER_KEY); } catch { /* ignore */ }
}

// ─── Level helpers ─────────────────────────────────────────────────────────────

export function getStoredLevel() {
    try { const v = localStorage.getItem(AUTH_LEVEL_KEY); return v !== null ? Number(v) : 0; } catch { return 0; }
}

function storeLevel(level) {
    try { localStorage.setItem(AUTH_LEVEL_KEY, String(level ?? 0)); } catch { /* ignore */ }
}

export function clearStoredLevel() {
    try { localStorage.removeItem(AUTH_LEVEL_KEY); } catch { /* ignore */ }
}

// ─── Core fetch wrapper ────────────────────────────────────────────────────────

/**
 * @param {string}  path           e.g. '/api/auth/me'
 * @param {object}  options        standard fetch options (method, body, …)
 * @param {boolean} authenticated  whether to add X-Session-Token header (default true)
 */
async function apiFetch(path, options = {}, authenticated = true) {
    const base = await getBaseURL();
    const url  = path.startsWith('http') ? path : `${base}${path}`;

    const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
    if (authenticated) {
        const token = getStoredToken();
        if (token) headers['X-Session-Token'] = token;
    }

    const res = await fetch(url, { ...options, headers });

    if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try { const j = await res.json(); msg = j.error || msg; } catch { /* ignore */ }
        throw new Error(msg);
    }
    return res.json();
}

// ─── Auth endpoints ────────────────────────────────────────────────────────────

/**
 * SSO login.
 * Saves sessionToken → 'auth_token' and user profile → 'auth_user'.
 * @param {'google'|'facebook'} provider
 * @param {string} token  id_token (Google) or accessToken (Facebook)
 * @returns {object} user profile (includes sessionToken field)
 */
export async function ssoLogin(provider, token) {
    const json = await apiFetch('/api/auth/sso', {
        method: 'POST',
        body: JSON.stringify({ provider, token }),
    }, false); // not yet authenticated — no token header
    if (!json.success) throw new Error(json.error || 'Login failed');
    // Persist token first so all subsequent calls carry it automatically.
    if (json.data?.sessionToken) storeToken(json.data.sessionToken);
    storeUser(json.data);
    storeLevel(json.data?.level);
    return json.data;
}

export const loginWithGoogle   = (idToken)     => ssoLogin('google',   idToken);
export const loginWithFacebook = (accessToken) => ssoLogin('facebook', accessToken);

/** Verify the stored token is still valid; refresh cached user profile. */
export async function fetchMe() {
    const json = await apiFetch('/api/auth/me');
    if (!json.success) throw new Error(json.error || 'Not authenticated');
    storeUser(json.data);
    storeLevel(json.data?.level);
    return json.data;
}

/** Revoke the token on the server and wipe both sessionStorage keys. */
export async function logout() {
    try { await apiFetch('/api/auth/logout', { method: 'POST' }); } catch { /* ignore */ }
    clearStoredToken();
    clearStoredUser();
    clearStoredLevel();
}

// ─── Bookmark endpoints ────────────────────────────────────────────────────────

export async function getBookmarks() {
    const json = await apiFetch('/api/auth/bookmark');
    if (!json.success) throw new Error(json.error || 'Failed to fetch bookmarks');
    return json.data;
}

export async function addBookmark(payload) {
    const json = await apiFetch('/api/auth/bookmark', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
    if (!json.success) throw new Error(json.error || 'Failed to add bookmark');
    return json.data;
}

export async function updateBookmark(id, payload) {
    const json = await apiFetch(`/api/auth/bookmark/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
    });
    if (!json.success) throw new Error(json.error || 'Failed to update bookmark');
    return json.data;
}

export async function deleteBookmark(id) {
    await apiFetch(`/api/auth/bookmark/${id}`, { method: 'DELETE' });
}
